import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { prefixClass } from 'lib/utils';
import { IconEmailNew } from 'components/Icons';
import { Controller, useForm } from 'react-hook-form';
import { AskRequest, AskRequestCreateType, CreateAskRequestEmail } from '@hone-automation/common';
import { useAuthContext } from 'context/useAuthContext';
import { useQueueContext } from 'presentation/components/Queue/QueueContext';
import { useCreateQueueRequestEmailMutation } from 'hooks/queue';
import KSInputField from 'presentation/components/KSInputField';
import KSCheckBox from 'presentation/components/KSCheckBox';
import { EmailAutocomplete } from 'presentation/components/EmailAutocomplete';
import CMComposer from 'presentation/components/Queue/CMComposer/CMComposer';
import { CMComposerContexts } from 'presentation/components/Queue/CMComposer/types';
import { MobileSlideUpPanel } from '../MobileSlideUpPanel/MobileSlideUpPanel';
import './MobileCreateEmailPanel.scss';

interface MobileCreateEmailPanelProps {
  isOpen: boolean;
  onClose: () => void;
  locationName?: string;
}

type FormValues = {
  subject: string;
  to: string;
  cc: string;
  isInternal: boolean;
};

const defaultValues: FormValues = {
  subject: '',
  to: '',
  cc: '',
  isInternal: false,
};

export const MobileCreateEmailPanel: React.FC<MobileCreateEmailPanelProps> = ({ isOpen, onClose, locationName }) => {
  const prefix = prefixClass('mobile-create-email-panel');
  const { locationId } = useParams();
  const { isBookkeeperAdmin } = useAuthContext();
  const { dispatch } = useQueueContext();

  const { control, reset, watch } = useForm<FormValues>({ defaultValues });
  const isInternal = watch('isInternal');

  const { mutate: createEmailRequest, isPending } = useCreateQueueRequestEmailMutation(
    locationId || '',
    (newRequest: AskRequest) => {
      dispatch({ type: 'SET_SELECTED_ITEM', payload: newRequest });
    }
  );

  const handleClose = useCallback(() => {
    reset(defaultValues);
    onClose();
  }, [reset, onClose]);

  const handleSubmit = useCallback(
    (formData: FormData, _createType: AskRequestCreateType) => {
      const formValues = watch();
      const messageContent = (formData.get('comment') as string) || '';

      const ccEmails = formValues.cc
        .split(',')
        .map(email => email.trim())
        .filter(email => email.length > 0);

      const emailData: CreateAskRequestEmail = {
        subject: formValues.subject,
        to: formValues.to,
        cc: ccEmails,
        content: messageContent,
        visibility: formValues.isInternal ? 'Internal' : 'All',
        locationId: locationId || '',
      };

      createEmailRequest(emailData, {
        onSuccess: () => handleClose(),
      });
    },
    [watch, locationId, createEmailRequest, handleClose]
  );

  return (
    <MobileSlideUpPanel
      isOpen={isOpen}
      onClose={handleClose}
      title="New Email"
      titleIcon={<IconEmailNew />}
      fullSizeOnOpen
      resizable={false}
    >
      <div className={prefix()}>
        <div className={prefix('__meta')}>
          {locationName && <span className={prefix('__location')}>{locationName}</span>}
        </div>

        <div className={prefix('__form')}>
          <div className={prefix('__row')}>
            <label className={prefix('__label')}>Subject</label>
            <Controller
              name="subject"
              control={control}
              render={({ field: { value, onChange } }) => (
                <KSInputField
                  type="text"
                  value={value}
                  placeholder="My new email"
                  onChange={e => onChange(e.target.value)}
                />
              )}
            />
          </div>

          <div className={prefix('__row')}>
            <label className={prefix('__label')}>To</label>
            <Controller
              name="to"
              control={control}
              render={({ field: { value, onChange } }) => (
                <EmailAutocomplete value={value} onChange={onChange} placeholder="kslocation@email.com" maxEmails={1} />
              )}
            />
          </div>

          <div className={prefix('__row')}>
            <label className={prefix('__label')}>CC</label>
            <Controller
              name="cc"
              control={control}
              render={({ field: { value, onChange } }) => (
                <EmailAutocomplete value={value} onChange={onChange} placeholder="email list separated by commas" />
              )}
            />
          </div>

          {isBookkeeperAdmin && (
            <div className={prefix('__frame')}>
              <Controller
                name="isInternal"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <KSCheckBox
                    rounded
                    label="Internal (Visible to KS Only)"
                    checked={value}
                    colorLabel="gray"
                    onChange={checked => onChange(checked)}
                  />
                )}
              />
            </div>
          )}
        </div>

        <div className={prefix('__composer')}>
          <CMComposer
            handleOnCallback={handleSubmit}
            hideReopenLabel
            messageLoading={isPending}
            context={CMComposerContexts.createRequest}
            isCreateMode
            isInternal={isInternal}
            submitLabel="SEND"
          />
        </div>
      </div>
    </MobileSlideUpPanel>
  );
};

MobileCreateEmailPanel.displayName = 'MobileCreateEmailPanel';

export default MobileCreateEmailPanel;
